import { useState, useEffect, useMemo, useRef, type ReactNode } from "react"
import { motion } from "framer-motion"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"
import { supabase, createNotification, saveSessionCompletion, saveSessionSetLogs, getCurrentBodyweightKg, getAthleteProfile, recordRmMeasurement, upsertAthleteProfile, type SessionSetLogInput, type RmHistoryPhase } from "@/lib/supabase"
import { useAnalytics } from "@/hooks/useAnalytics"
import { useAuth } from "@/contexts/AuthContext"
import { useSubscription } from "@/hooks/useSubscription"
import type { SessionData, SessionExercise } from "@/types/session"
import type { SetScheme, SetSchemeMethod } from "@/types/session"
import type { ChargeDisplay } from "@/lib/exerciseNormalization"
import {
  getProgrammeIdFromWizard,
  getProgramMeta,
  getNextSessionNumber,
  getProgramSession,
  getWeekSessionNumbers,
  mapProgramSessionToSessionData,
  savePendingSession,
  enrichExercisesWithVideos,
  enrichWarmupWithVideos,
  computeChargeKg,
  splitCombinedField,
  parseSetsCount,
  type AthleteOneRm,
  type AthleteLevel,
} from "@/lib/programs"
// Thème, icônes, badges et chronos — partagés avec le parcours de test de force.
import { T, CSS, I, Badge, TimerCountdown, TimerButton, clearCountdown } from "@/components/session/sessionUi"
import { StrengthTestBlock, strengthTestRestKey, type StrengthTestResult } from "@/components/session/StrengthTestBlock"
import { ExerciseParams, METHOD_LABEL } from "@/components/session/ExerciseParams"
import { resolveSetScheme } from "@/lib/setScheme"
import {
  buildTestReferenceOneRm,
  resolveTestedLift,
  testProtocolForLevel,
} from "@/lib/strengthTest"
import {
  RM_FIELD_BY_MOVEMENT,
  resolveAgeBracket,
  toStandardsGender,
} from "@/lib/strengthStandards"
import { toLocalISODate } from "@/lib/utils"
import {
  loadSessionProgress,
  saveSessionProgress,
  clearSessionProgress,
  purgeExpiredSessionEntries,
  loadTimerState,
  saveTimerState,
} from "@/lib/sessionResume"

// ═══ SESSION PROGRESS ══════════════════════════════════════
// Position dans la séance, sauvegardée à chaque changement (voir lib/sessionResume).
// Champs optionnels : une entrée écrite par une version précédente peut en manquer.
type SavedProgress = {
  ph?: string
  prevPh?: string
  ei?: number
  rnd?: number
  sd?: Record<string, boolean>
  wd?: number[]
  cd?: number[]
  fd?: number[]
  ts?: Record<number, { set: number; resting: boolean }>
}

// ═══ CONGRATS ══════════════════════════════════════════════
const CONGRATS = ["Bien joué","Bravo","Excellent, le coach est fier de ton effort","Tu as tout donné","Chapeau","Impressionnant","Force mentale","Rien ne t'arrête","Tu repousses tes limites","Quelle détermination"]

// ═══ SUB-COMPONENTS ════════════════════════════════════════

function VideoPlayer({ url, fs, toggle }: { url: string; fs: boolean; toggle: () => void }) {
  // Ratio réel de la vidéo (détecté au chargement) — évite le rognage des vidéos portrait
  // qu'imposerait un aspectRatio fixe en 16/9.
  const [ratio, setRatio] = useState<number | null>(null)
  // Tant que le ratio est inconnu, la vidéo n'a ni sa taille ni sa première image : la
  // laisser visible affichait un aplat de couleur plein écran, le temps du chargement.
  // On réserve la place (aspectRatio 16/9 par défaut, pour ne pas faire sauter la mise
  // en page à l'arrivée du vrai ratio) et on n'y met qu'un spinner — jamais de fond
  // peint. `background:"transparent"` sur la balise vidéo est la moitié qui compte :
  // sans lui, le navigateur peint le rectangle avant même d'avoir une image.
  const ready = ratio !== null
  const plate = (
    <div style={{ position:"relative" as const,width:"100%",aspectRatio: ratio ? `${ratio}` : "16/9" }}>
      <video
        src={url}
        onLoadedMetadata={e => {
          const v = e.currentTarget
          if (v.videoWidth && v.videoHeight) setRatio(v.videoWidth / v.videoHeight)
        }}
        // Vidéo injoignable : on arrête le spinner et on laisse le cadre vide plutôt
        // que de faire tourner l'anneau indéfiniment.
        onError={() => setRatio(16 / 9)}
        style={{ width:"100%",height:"100%",borderRadius:12,objectFit:"contain" as const,background:"transparent",opacity: ready ? 1 : 0,transition:"opacity 0.18s ease" }}
        autoPlay loop muted playsInline
      />
      {!ready && (
        <div style={{ position:"absolute" as const,inset:0,display:"grid",placeItems:"center",pointerEvents:"none" as const }}>
          <div style={{ width:26,height:26,borderRadius:"50%",border:`2px solid ${T.cy}33`,borderTopColor:T.cy,animation:"spin 0.9s linear infinite" }} />
        </div>
      )}
    </div>
  )
  if (fs) return <div style={{ position:"fixed" as const,inset:0,zIndex:100,background:"#000",display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}><div style={{ width:"90%",maxWidth:420 }}>{plate}</div><button onClick={toggle} style={{ position:"absolute" as const,top:20,right:20,background:"rgba(255,255,255,0.07)",border:"none",borderRadius:"50%",width:44,height:44,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",cursor:"pointer" }}>{I.x}</button></div>
  return <div style={{ position:"relative" as const,borderRadius:14,overflow:"hidden" as const,border:`1px solid ${T.bd}` }}>{plate}<button onClick={toggle} style={{ position:"absolute" as const,top:10,right:10,background:"rgba(0,0,0,0.4)",backdropFilter:"blur(8px)",border:"none",borderRadius:8,width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",color:T.t2,cursor:"pointer" }}>{I.exp}</button></div>
}

function AnatomyImage({ url }: { url?: string }) {
  if (!url) return null
  return <img src={url} alt="Muscles sollicités" style={{ width:130,height:"auto",borderRadius:10,opacity:0.9 }} />
}

// AudioContext créé/déverrouillé au premier clic (geste utilisateur) puis réutilisé et
// réveillé (resume()) à chaque retour au premier plan — iOS suspend le contexte en arrière-plan.
function playRestDoneBeep(ctx: AudioContext | null) {
  if (!ctx) return
  try {
    void ctx.resume()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.frequency.value = 880
    osc.connect(gain)
    gain.connect(ctx.destination)
    gain.gain.setValueAtTime(0.2, ctx.currentTime)
    osc.start()
    osc.stop(ctx.currentTime + 0.35)
  } catch { /* audio unavailable — flash-only fallback */ }
}

// Persistance du chrono repos — localStorage (voir lib/sessionResume), clé = restKey
// (même clé que le remount React). Nécessaire car un ref meurt avec le composant : iOS
// décharge la page au verrouillage d'écran, et ferme carrément l'app installée en
// arrière-plan — sessionStorage n'y survit pas.
type SavedRestState = { sec: number; status: "running" | "paused" | "done"; deadline?: number; remainingAtPause?: number }

function loadRestState(key: string): SavedRestState | null {
  return loadTimerState<SavedRestState>(key)
}
function saveRestState(key: string, state: SavedRestState) {
  saveTimerState(key, state)
}

// Chrono repos entre séries : cliquable (pause/reprise), anneau de progression,
// bip + flash à 0. Basé sur une deadline absolue (Date.now()) plutôt qu'un
// décompte par tick, pour rester correct après une mise en arrière-plan ou un
// démontage/remontage réel (voir SavedRestState ci-dessus).
function RestTimer({ sec, sub, restKey }: { sec: number; sub?: string; restKey: string }) {
  const storageKey = `rest:${restKey}`
  const [rem, setRem] = useState(sec)
  const [running, setRunning] = useState(false)
  const [flash, setFlash] = useState(false)
  const deadlineRef = useRef<number | null>(null) // Date.now() cible quand running
  const audioCtxRef = useRef<AudioContext | null>(null)

  const ensureAudioCtx = () => {
    if (!audioCtxRef.current) {
      try {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        audioCtxRef.current = new Ctx()
      } catch { audioCtxRef.current = null }
    }
    return audioCtxRef.current
  }

  // Hydratation au montage : même restKey retrouvée en storage → restauration
  // (running resynchronisé sur la deadline sauvegardée, paused figé tel quel) ;
  // restKey absente/sec différent → tuile neuve (reset propre au changement d'exercice).
  useEffect(() => {
    const saved = loadRestState(storageKey)
    if (saved && saved.sec === sec) {
      if (saved.status === "running" && typeof saved.deadline === "number") {
        const left = Math.max(0, Math.ceil((saved.deadline - Date.now()) / 1000))
        if (left > 0) {
          deadlineRef.current = saved.deadline
          setRem(left); setRunning(true); setFlash(false)
          return
        }
        saveRestState(storageKey, { sec, status: "done" })
        setRem(0); setRunning(false); deadlineRef.current = null; setFlash(false)
        return
      }
      if (saved.status === "paused" && typeof saved.remainingAtPause === "number") {
        setRem(saved.remainingAtPause); setRunning(false); deadlineRef.current = null; setFlash(false)
        return
      }
      if (saved.status === "done") { setRem(0); setRunning(false); deadlineRef.current = null; setFlash(false); return }
    }
    setRem(sec); setRunning(false); deadlineRef.current = null; setFlash(false)
  }, [storageKey, sec])

  useEffect(() => () => { void audioCtxRef.current?.close().catch(() => {}) }, [])

  // Tant que ça tourne, on recalcule depuis la deadline (pas un décrément par tick) —
  // ainsi un retour au premier plan après throttling/suspension retombe juste.
  useEffect(() => {
    if (!running) return
    const iv = setInterval(() => {
      if (deadlineRef.current == null) return
      const left = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000))
      setRem(left)
      if (left <= 0) {
        setRunning(false)
        deadlineRef.current = null
        saveRestState(storageKey, { sec, status: "done" })
        if (document.visibilityState === "visible") {
          setFlash(true)
          playRestDoneBeep(ensureAudioCtx())
          setTimeout(() => setFlash(false), 1500)
        }
      }
    }, 250)
    return () => clearInterval(iv)
  }, [running, storageKey, sec])

  // Retour au premier plan : resync immédiat + réveil de l'AudioContext (iOS le suspend
  // en arrière-plan). Si le chrono a expiré pendant l'absence, on passe en "terminé"
  // silencieusement — pas de bip rétroactif.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return
      void audioCtxRef.current?.resume().catch(() => {})
      if (!running || deadlineRef.current == null) return
      const left = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000))
      setRem(left)
      if (left <= 0) {
        setRunning(false); deadlineRef.current = null
        saveRestState(storageKey, { sec, status: "done" })
      }
    }
    document.addEventListener("visibilitychange", onVisible)
    return () => document.removeEventListener("visibilitychange", onVisible)
  }, [running, storageKey, sec])

  if (sec <= 0) {
    return (
      <div style={{ marginTop:11,display:"flex",alignItems:"center",gap:12,background:T.s1,border:`1px solid ${T.line}`,borderRadius:14,padding:"12px 14px" }}>
        <span style={{ fontSize:14.5,fontWeight:600,color:T.t2 }}>Enchaîner</span>
        <span style={{ fontSize:12,color:T.t3,marginLeft:"auto" }}>{sub || "sans récupération"}</span>
      </div>
    )
  }

  const done = rem <= 0
  const warn = !done && rem <= 10
  const paused = !done && !running && rem !== sec
  const status: "idle" | "running" | "paused" | "done" = done ? "done" : running ? "running" : paused ? "paused" : "idle"

  const toggle = () => {
    const ctx = ensureAudioCtx()
    void ctx?.resume().catch(() => {})
    if (rem <= 0) {
      const deadline = Date.now() + sec * 1000
      deadlineRef.current = deadline
      setRem(sec); setRunning(true); setFlash(false)
      saveRestState(storageKey, { sec, status: "running", deadline })
      return
    }
    if (running) {
      let left = rem
      if (deadlineRef.current != null) left = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000))
      setRem(left)
      deadlineRef.current = null
      setRunning(false)
      saveRestState(storageKey, { sec, status: "paused", remainingAtPause: left })
    } else {
      const deadline = Date.now() + rem * 1000
      deadlineRef.current = deadline
      setRunning(true)
      saveRestState(storageKey, { sec, status: "running", deadline })
    }
  }

  const labelText = status === "running" ? "Repos en cours" : status === "paused" ? "Repos en pause" : status === "done" ? "Repos terminé" : "Lancer le repos"

  // Bouton de repos : une seule cible, pleine largeur, compacte (~62 px). Le chrono est
  // le plus gros texte du bloc — c'est la seule chose qu'on y lit à bout de bras
  // pendant la récupération ; le libellé et la nature du repos s'empilent en petit à
  // sa gauche. L'anneau de 44 px de l'ancienne tuile a laissé la place à la barre du
  // bas, qui dit la même chose sur toute la largeur.
  const accent = flash ? "#ef4444" : warn ? T.or : status === "done" ? T.gn : T.cy
  const fraction = status === "done" ? 1 : status === "idle" ? 0 : 1 - rem / sec
  const hint = status === "done" ? "Appuie pour relancer" : sub

  return (
    <motion.button
      whileTap={{ scale: 0.985 }}
      onClick={toggle}
      style={{
        marginTop:11,width:"100%",display:"flex",alignItems:"center",gap:11,position:"relative" as const,
        background:flash ? "rgba(239,68,68,0.15)" : `${accent}1a`,
        border:`1px solid ${accent}6b`,borderRadius:14,padding:"11px 13px 10px",cursor:"pointer",
        textAlign:"left" as const,fontFamily:"inherit",transition:"background 0.16s,border-color 0.2s",
      }}
    >
      <span style={{ width:28,height:28,flex:"0 0 28px",borderRadius:"50%",background:accent,display:"grid",placeItems:"center",color:"#032029" }}>
        {status === "done" ? I.chk : status === "running" ? I.pause : I.playSm}
      </span>
      {/* À gauche l'ÉTAT du chrono, seul. À droite le temps, et sous lui la MODALITÉ du
          repos ("passif · assis") : elle qualifie la récupération, pas l'état du bouton,
          et sous le libellé d'état elle se lisait comme sa précision. */}
      <span style={{ flex:1,minWidth:0,fontSize:13,fontWeight:600,color:accent }}>{labelText}</span>
      <span style={{ flex:"0 0 auto",textAlign:"right" as const,minWidth:0 }}>
        <span style={{ display:"block",fontSize:24,fontWeight:600,fontVariantNumeric:"tabular-nums" as const,color:T.txHi,letterSpacing:-0.4,lineHeight:1.1 }}>
          {Math.floor(rem / 60)}:{(rem % 60).toString().padStart(2,"0")}
        </span>
        {hint && <span style={{ display:"block",fontSize:11,color:T.t3,lineHeight:1.3,whiteSpace:"nowrap" as const }}>{hint}</span>}
      </span>
      <span style={{ position:"absolute" as const,left:13,right:13,bottom:6,height:2,borderRadius:2,background:"rgba(255,255,255,0.06)",overflow:"hidden" as const }}>
        <span style={{ display:"block",height:"100%",width:`${fraction * 100}%`,background:accent,transition:"width 0.25s linear" }} />
      </span>
    </motion.button>
  )
}

// ─── Renders cues / errors / breathing for one ExerciseInstructions ───
function renderInstrContent(instr: import("@/types/session").ExerciseInstructions) {
  return (
    <>
      {instr.cues.length > 0 && (
        <div style={{ marginBottom:instr.errors.length > 0 || instr.breathing ? 14 : 0 }}>
          <div style={{ display:"flex",alignItems:"center",gap:5,marginBottom:8 }}>
            <div style={{ width:6,height:6,borderRadius:"50%",background:T.cy,flexShrink:0 }} />
            <span style={{ fontSize:10,fontWeight:600,color:T.cy,letterSpacing:0.8,textTransform:"uppercase" as const }}>Points de forme</span>
          </div>
          {instr.cues.map((c, i) => (
            <div key={i} style={{ display:"flex",gap:8,padding:"4px 0",fontSize:13,color:T.t2,lineHeight:1.5 }}>
              <span style={{ color:T.cy,opacity:0.5,flexShrink:0 }}>•</span>
              <span>{c}</span>
            </div>
          ))}
        </div>
      )}
      {instr.errors.length > 0 && (
        <div style={{ marginBottom:instr.breathing ? 14 : 0 }}>
          <div style={{ display:"flex",alignItems:"center",gap:5,marginBottom:8 }}>
            <div style={{ width:6,height:6,borderRadius:"50%",background:T.or,flexShrink:0 }} />
            <span style={{ fontSize:10,fontWeight:600,color:T.or,letterSpacing:0.8,textTransform:"uppercase" as const }}>Erreurs fréquentes</span>
          </div>
          {instr.errors.map((e, i) => (
            <div key={i} style={{ display:"flex",gap:8,padding:"4px 0",fontSize:13,color:T.t2,lineHeight:1.5 }}>
              <span style={{ color:T.or,opacity:0.5,flexShrink:0 }}>•</span>
              <span>{e}</span>
            </div>
          ))}
        </div>
      )}
      {instr.breathing && (
        <div>
          <div style={{ display:"flex",alignItems:"center",gap:5,marginBottom:8 }}>
            <div style={{ width:6,height:6,borderRadius:"50%",background:T.vi,flexShrink:0 }} />
            <span style={{ fontSize:10,fontWeight:600,color:T.vi,letterSpacing:0.8,textTransform:"uppercase" as const }}>Respiration</span>
          </div>
          <div style={{ fontSize:13,color:T.t2,lineHeight:1.5 }}>{instr.breathing}</div>
        </div>
      )}
    </>
  )
}

// ═══ EXERCISE CARD (used in exercise phase) ════════════════
/**
 * `testBlock` remplace les tuiles Séries/Reps/Charge/Tempo/Repos par le parcours de test
 * de force, et RIEN d'autre : titre, catégorie, muscles, schéma musculaire, instructions
 * techniques et vidéos restent rendus ici, à l'identique. Le test change ce que le client
 * doit faire, pas ce qu'il doit savoir du mouvement.
 */
// Exporté pour le banc de rendu (`scripts/dev-exercise-preview`) : c'est le seul moyen
// de regarder cet écran dans un navigateur sans passer par l'auth et une séance réelle.
export function ExerciseView({ ex, oneRm, bodyweightKg, level, onOpenVideo, restKey, testBlock, scheme }: { ex: SessionExercise; oneRm: AthleteOneRm | null; bodyweightKg: number | null; level: AthleteLevel; onOpenVideo: (url: string) => void; restKey: string; testBlock?: ReactNode; scheme: SetScheme | null }) {
  const [instrOpen, setInstrOpen] = useState(false)
  const charge = computeChargeKg(ex.canonicalName, ex.charge, oneRm, bodyweightKg, level)
  return (
    <>
      {/* Entête — titre, catégorie, muscles */}
      <div style={{ display:"flex",alignItems:"flex-start",gap:8,marginTop:8,marginBottom:4 }}>
        <div style={{ flex:1,minWidth:0 }}>
          <h2 style={{ fontSize:29,fontWeight:700,lineHeight:1.12,letterSpacing:-0.3,color:T.txHi }}>{ex.canonicalName}</h2>
          <div style={{ display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" as const,margin:"11px 0 18px" }}>
            <span style={{ fontSize:13,fontWeight:500,padding:"7px 11px",borderRadius:8,background:"rgba(6,182,212,0.13)",color:"#5fd8ea" }}>{ex.category}</span>
            <span style={{ fontSize:14,fontWeight:500,color:T.txHi,display:"flex",alignItems:"center",gap:7 }}>
              <span style={{ width:7,height:7,borderRadius:"50%",background:"#f2545b",display:"inline-block" }} />
              {ex.primaryMuscle}
              {ex.secondaryMuscle && (
                <em style={{ fontStyle:"normal" as const,color:T.t3,display:"flex",alignItems:"center",gap:7 }}>
                  · <span style={{ width:7,height:7,borderRadius:"50%",background:"#4f86d6",display:"inline-block" }} />
                  {ex.secondaryMuscle}
                </em>
              )}
            </span>
          </div>
        </div>
        {/* Schéma musculaire (Supabase) — hors maquette, conservé : c'est une donnée
            réelle de la table `exercises`, que la maquette n'avait pas à modéliser. */}
        {ex.anatomyImageUrl && <AnatomyImage url={ex.anatomyImageUrl} />}
      </div>

      {testBlock ?? (
        <>
          <ExerciseParams ex={ex} scheme={scheme} charge={charge} />
          <RestTimer key={restKey} restKey={restKey} sec={ex.restBetweenSets} sub={ex.restExplain} />
        </>
      )}

      {/* Instructions biomécaniques (DB) */}
      {(ex.instructionsDb || ex.instructionsDbParts?.some(p => p.instructions !== null)) && (
        // 16 px de respiration entre le bouton de repos et l'accordéon : collés, les
        // deux se lisaient comme un seul bloc, alors que l'un est une action et
        // l'autre une lecture.
        <div style={{ marginTop:16,marginBottom:16,border:`1px solid ${instrOpen ? T.cy+"40" : T.bd}`,borderRadius:14,overflow:"hidden" as const,transition:"border-color 0.2s" }}>
          <button onClick={() => setInstrOpen(o => !o)} style={{ width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",background:T.sf,border:"none",borderBottom:instrOpen ? `1px solid rgba(6,182,212,0.15)` : "none",padding:"12px 16px",cursor:"pointer" }}>
            <div style={{ display:"flex",alignItems:"center",gap:6 }}>
              <span style={{ color:T.cy,opacity:0.55 }}>{I.inf}</span>
              <span style={{ fontSize:13,fontWeight:600,color:instrOpen ? T.cy : T.t2,transition:"color 0.2s" }}>Instructions techniques</span>
            </div>
            <span style={{ fontSize:11,color:T.t3,display:"inline-block",transform:instrOpen ? "rotate(180deg)" : "rotate(0deg)",transition:"transform 0.25s" }}>▾</span>
          </button>
          {instrOpen && (
            <div style={{ background:T.sf,padding:"14px 16px" }}>
              {/* Combined: render each part that has instructions, separated by "+" */}
              {ex.instructionsDbParts
                ? ex.instructionsDbParts
                    .filter(p => p.instructions !== null)
                    .map((p, idx, arr) => (
                      <div key={idx}>
                        {/* Part name header */}
                        <div style={{ fontSize:11,fontWeight:700,color:T.cy,letterSpacing:0.8,textTransform:"uppercase" as const,marginBottom:10,opacity:0.9 }}>{p.name}</div>
                        {renderInstrContent(p.instructions!)}
                        {/* "+" separator between parts */}
                        {idx < arr.length - 1 && (
                          <div style={{ display:"flex",alignItems:"center",gap:10,margin:"14px 0" }}>
                            <div style={{ flex:1,height:1,background:"rgba(6,182,212,0.15)" }} />
                            <span style={{ fontSize:15,fontWeight:700,color:T.cy,opacity:0.6 }}>+</span>
                            <div style={{ flex:1,height:1,background:"rgba(6,182,212,0.15)" }} />
                          </div>
                        )}
                      </div>
                    ))
                : renderInstrContent(ex.instructionsDb!)
              }
            </div>
          )}
        </div>
      )}

      {/* Vidéo(s) — une par mouvement pour un exercice combiné, sinon une seule */}
      {ex.videoUrlParts && ex.videoUrlParts.length > 0 ? (
        <div style={{ display:"flex",flexDirection:"column" as const,gap:10 }}>
          {ex.videoUrlParts.map((p, i) => (
            <div key={i}>
              {ex.videoUrlParts!.length > 1 && (
                <div style={{ fontSize:11,fontWeight:700,color:T.cy,letterSpacing:0.8,textTransform:"uppercase" as const,marginBottom:6,opacity:0.9 }}>{p.name}</div>
              )}
              <VideoPlayer url={p.url} fs={false} toggle={() => onOpenVideo(p.url)} />
            </div>
          ))}
        </div>
      ) : (
        ex.videoUrl && <VideoPlayer url={ex.videoUrl} fs={false} toggle={() => onOpenVideo(ex.videoUrl!)} />
      )}
    </>
  )
}

// ═══ OVERVIEW HELPERS ══════════════════════════════════════

/**
 * Extrait le label méthode à afficher depuis format_travail.
 * Règle 1 : "Adaptation" dans displayName → null (tile non affichée).
 * Règle 2 : contient "1RM" → segment juste après "1RM + séparateur".
 * Règle 3 : cas général → premier segment avant le premier séparateur.
 * Séparateurs reconnus : — (tiret cadratin) et · (point médian).
 */
function extractMethod(formatLabel: string, displayName: string): string | null {
  if (displayName.includes("Adaptation")) return null
  const SEP = /—|·/
  const m1rm = /1RM/i.exec(formatLabel)
  if (m1rm) {
    const after = formatLabel.slice(m1rm.index + 3).replace(/^\s*[—·,.\-]\s*/, "")
    const next = SEP.exec(after)
    return (next ? after.slice(0, next.index) : after).trim() || null
  }
  const first = SEP.exec(formatLabel)
  return (first ? formatLabel.slice(0, first.index) : formatLabel).trim() || null
}

/**
 * Tempo écrit entre parenthèses dans `format_travail` — "(30X0)", "(X0X0)", "(2010)" :
 * quatre positions, chacune un chiffre ou un X. Ce n'est pas un nom de méthode, et le
 * tempo a déjà sa propre tuile sur l'écran d'exercice.
 */
const TEMPO_PAREN_RE = /\s*\(\s*[0-9Xx]{4}\s*\)/g

/**
 * Ce qu'affiche la carte « Méthode du jour » : les noms de méthodes DISTINCTS de la
 * séance, et rien d'autre — « Rest-Pause / Bi-set / Drop set ».
 *
 * Source de vérité : `set_scheme.method` des exercices, le MÊME champ qui pose les
 * badges REST-PAUSE / BI-SET / DROP SET sur les cartes d'exercice. La carte et les
 * badges disent donc forcément la même chose, avec les mêmes mots (`METHOD_LABEL`).
 * `format_travail`, lui, mélange méthode et exercice associé et traîne le tempo
 * derrière : « Rest-Pause T-Bar + Bi-set + RP Curl + Drop Set (30X0) ».
 *
 * Repli quand aucun exercice ne porte de schéma structuré (séries droites, ou séance
 * que la migration n'a pas encore couverte) : `format_travail` comme avant, tempo
 * entre parenthèses retiré.
 */
function methodOfDay(s: SessionData): string | null {
  if (s.displayName.includes("Adaptation")) return null
  const methods: SetSchemeMethod[] = []
  for (const e of s.exercises) {
    const m = e.setSchemeSpec?.method
    if (m && !methods.includes(m)) methods.push(m)
  }
  if (methods.length > 0) return methods.map(m => METHOD_LABEL[m].long).join(" / ")
  const fallback = extractMethod(s.formatLabel, s.displayName)
  return fallback ? fallback.replace(TEMPO_PAREN_RE, "").trim() || null : null
}

const MONO = "'SF Mono',ui-monospace,Menlo,Consolas,monospace"

/**
 * Une teinte par muscle principal, attribuée dans l'ordre d'apparition des exercices.
 * La même teinte sert à la pill sous le titre et au filet de la carte d'exercice : c'est
 * ce qui relie « Biceps » en haut de page à Barbell Curl plus bas.
 */
const MUSCLE_PALETTE = ["#f2545b", "#60a5fa", "#a78bfa", "#34d399", "#fbbf24", "#f472b6", "#2dd4bf"]

/**
 * Groupe musculaire d'un exercice : le muscle principal sans sa précision entre
 * parenthèses — « Biceps (étirement) » et « Biceps » sont un seul groupe, une seule pill,
 * une seule teinte.
 */
function muscleGroup(e: SessionExercise): string {
  return (e.primaryMuscle ?? "").replace(/\s*\([^)]*\)/g, "").trim()
}

function muscleColors(exercises: SessionExercise[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const e of exercises) {
    const m = muscleGroup(e)
    if (m && !map.has(m)) map.set(m, MUSCLE_PALETTE[map.size % MUSCLE_PALETTE.length])
  }
  return map
}

const METHOD_COLOR: Record<SetSchemeMethod, string> = {
  "rest-pause": "#f472b6",
  "bi-set": "#22d3ee",
  "drop-set": "#fb923c",
  "pyramide-montante": "#4ade80",
  cluster: "#a78bfa",
  "myo-reps": "#facc15",
}

/**
 * Prescription d'un exercice sur une ligne : « 4 × 10+5+3 » et la charge. Les paliers
 * d'une méthode composée sont joints avec le même séparateur que l'écran d'exercice
 * (« + » mini-séries, « → » changement de charge, « + » espacé pour un bi-set).
 */
function prescription(e: SessionExercise, charge: ChargeDisplay): { reps: string; load: string } {
  const spec = e.setSchemeSpec
  const sep = !spec ? "" : spec.method === "bi-set" ? " + " : spec.method === "drop-set" || spec.method === "pyramide-montante" ? "→" : "+"
  const reps = spec ? spec.paliers.map(p => p.reps).join(sep) : e.reps
  const n = parseInt(e.sets, 10)
  const sets = Number.isFinite(n) ? `${n} × ` : `${e.sets} · `
  return { reps: `${sets}${reps}`, load: charge.movements.map(m => m.weight).filter(Boolean).join(" / ") }
}

/** Somme des « N min » d'un texte : « 5 min vélo + 5 min rameur » → 10. */
function minutesIn(text: string): number {
  return [...text.matchAll(/(\d+)\s*min/g)].reduce((acc, m) => acc + parseInt(m[1], 10), 0)
}

function todayLabel(): string {
  const d = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "short" })
  return d.charAt(0).toUpperCase() + d.slice(1)
}

function SectionHead({ title, color, badge, icon }: { title: string; color: string; badge?: string; icon?: ReactNode }) {
  return (
    <div style={{ display:"flex",alignItems:"center",gap:9,marginBottom:12 }}>
      <div style={{ width:3,height:18,borderRadius:2,background:color,boxShadow:`0 0 10px ${color}66` }} />
      <span style={{ fontSize:15,fontWeight:700,color:T.txHi }}>{title}</span>
      {icon && <span style={{ color,display:"flex",opacity:0.7 }}>{icon}</span>}
      {badge && (
        <span style={{ marginLeft:"auto",fontFamily:MONO,fontSize:11,fontWeight:600,color,background:`${color}14`,border:`1px solid ${color}40`,borderRadius:6,padding:"3px 8px" }}>{badge}</span>
      )}
    </div>
  )
}

/** Durée en pastille sombre, à droite d'une ligne d'échauffement. */
const DurationChip = ({ text }: { text: string }) => (
  <span style={{ fontFamily:MONO,fontSize:11,fontWeight:600,color:T.t2,background:"rgba(148,163,184,0.08)",borderRadius:6,padding:"4px 8px",whiteSpace:"nowrap" as const }}>{text}</span>
)

function StatCard({ icon, label, value, accent }: { icon: ReactNode; label: string; value: string; accent: string }) {
  // « 61 min » → le chiffre en grand, l'unité en petit dans la teinte de la carte.
  const m = /^(\d+)\s*(.*)$/.exec(value.trim())
  return (
    <div style={{ background:T.s1,border:`1px solid ${T.line}`,borderRadius:14,padding:"11px 12px 12px",position:"relative" as const,overflow:"hidden" as const }}>
      <div style={{ position:"absolute" as const,left:0,right:0,top:0,height:"55%",background:`linear-gradient(180deg,${accent},transparent)`,opacity:0.1,pointerEvents:"none" as const }} />
      <div style={{ position:"absolute" as const,left:0,right:0,top:0,height:2,background:accent,opacity:0.7,pointerEvents:"none" as const }} />
      <div style={{ position:"relative" as const }}>
        <div style={{ color:accent,display:"flex",marginBottom:8 }}>{icon}</div>
        <div style={{ fontSize:10,fontWeight:600,letterSpacing:0.9,textTransform:"uppercase" as const,color:T.t3,marginBottom:3 }}>{label}</div>
        {m ? (
          <div style={{ display:"flex",alignItems:"baseline",gap:3 }}>
            <span style={{ fontSize:26,fontWeight:700,color:T.txHi,fontVariantNumeric:"tabular-nums" as const,letterSpacing:-0.5,lineHeight:1.1 }}>{m[1]}</span>
            {m[2] && <span style={{ fontSize:12,fontWeight:600,color:accent }}>{m[2]}</span>}
          </div>
        ) : (
          <div style={{ fontSize:20,fontWeight:700,color:T.txHi,lineHeight:1.2 }}>{value}</div>
        )}
      </div>
    </div>
  )
}

// ═══ MAIN ══════════════════════════════════════════════════
export default function SessionPage() {
  const nav = useNavigate()
  const { wizardData, user, wizardLoading } = useAuth()
  // Le prénom vient du diagnostic enregistré. Il était lu dans l'ancien localStorage
  // `fitness_app_session`, que seule `CompletionPage` remplissait — un écran jamais atteint :
  // tous les clients étaient félicités en « Athlète ».
  const rawFirstName = wizardData?.first_name?.trim()
  const firstName = rawFirstName
    ? rawFirstName.charAt(0).toUpperCase() + rawFirstName.slice(1).toLowerCase()
    : "Athlète"

  const { loading: subLoading } = useSubscription()
  const level: AthleteLevel = wizardData?.level_id === "intermediate" || wizardData?.level_id === "advanced"
    ? wizardData.level_id
    : "beginner"

  const [sessionData, setSessionData] = useState<SessionData | null>(null)
  const [sessionLoading, setSessionLoading] = useState(true)
  // Rang de la séance dans sa semaine de programme (« J2 sur 4 ») — null si inconnu.
  const [weekPos, setWeekPos] = useState<{ day: number; total: number } | null>(null)

  const [ph, setPh] = useState<string>("overview")
  const [prevPh, setPrevPh] = useState("")
  const [rnd, setRnd] = useState(1)
  const [ei, setEi] = useState(0)
  const [sd, setSd] = useState<Record<string, boolean>>({})
  const [wd, setWd] = useState<number[]>([])
  const [cd, setCd] = useState<number[]>([])
  const [fd, setFd] = useState<number[]>([])
  const [fsV, setFsV] = useState<string | null>(null)
  const [an, setAn] = useState(false)
  const [oneRm, setOneRm] = useState<AthleteOneRm | null>(null)
  const [bodyweightKg, setBodyweightKg] = useState<number | null>(null)
  const [doneSaving, setDoneSaving] = useState(false)
  const notifiedDone = useRef(false)
  const programmeRef = useRef<{ id: string; num: number } | null>(null)

  // ── Test de force ───────────────────────────────────────
  // Séances de test du bloc, telles que la table `programs` les déclare. C'est le SEUL
  // signal qui distingue une séance de test : ni le libellé, ni `format_travail`, qui
  // varient d'un programme à l'autre (« Testing », « Testing 1RM », « Re-Testing 1RM »…).
  const [testingSessions, setTestingSessions] = useState<number[]>([])
  // Où en est chaque mouvement du test : index de série 0-2, et repos en cours ou non.
  // Clé = index d'exercice, pour survivre au va-et-vient entre mouvements.
  const [ts, setTs] = useState<Record<number, { set: number; resting: boolean }>>({})
  const [testSaving, setTestSaving] = useState(false)

  const { track } = useAnalytics()
  const hasTrackedStart = useRef(false)

  // 1RM athlète + poids de corps (pour afficher la charge en kg à partir du %1RM)
  //
  // Passe par getAthleteProfile() — qui normalise les colonnes rm_* en nombres — plutôt
  // que par une requête brute : `rm_*` est `numeric` depuis la migration
  // `20261001120000` (le test de force arrondit au demi-kilo), et PostgREST sérialise le
  // numeric en CHAÎNE. Une requête directe ici recevrait "65.5" au lieu de 65.5, et
  // `computeChargeKg`/`comparableValue` (qui testent `typeof x === "number"`) écarteraient
  // silencieusement un 1RM pourtant bien renseigné.
  useEffect(() => {
    if (!user) return
    void (async () => {
      const profile = await getAthleteProfile()
      if (profile) {
        setOneRm({
          rm_bench_press: profile.rm_bench_press,
          rm_back_squat: profile.rm_back_squat,
          rm_deadlift: profile.rm_deadlift,
          rm_overhead_press: profile.rm_overhead_press,
          rm_pull_up: profile.rm_pull_up,
        })
      }
    })()
    void (async () => {
      const kg = await getCurrentBodyweightKg(user.id)
      if (kg != null) setBodyweightKg(kg)
    })()
  }, [user])

  useEffect(() => { purgeExpiredSessionEntries() }, [])

  // Load session from programme catalogue
  useEffect(() => {
    if (!user) return
    if (wizardLoading) return
    if (!wizardData) {
      setSessionLoading(false)
      setSessionData(null)
      return
    }
    void (async () => {
      setSessionLoading(true)
      try {
        const pid = getProgrammeIdFromWizard(wizardData)
        const meta = await getProgramMeta(pid)
        if (!meta) { setSessionData(null); return }
        setTestingSessions(meta.testing_sessions)
        const nextNum = await getNextSessionNumber(user.id, meta.sessions_total, pid)
        const ps = await getProgramSession(pid, nextNum)
        if (!ps) { setSessionData(null); return }
        programmeRef.current = { id: pid, num: nextNum }
        const mapped = mapProgramSessionToSessionData(ps)
        const [enrichedExercises, enrichedWarmup, weekNums] = await Promise.all([
          enrichExercisesWithVideos(mapped.exercises),
          enrichWarmupWithVideos(mapped.warmup),
          getWeekSessionNumbers(pid, ps.week),
        ])
        const sessionWithVideos = { ...mapped, exercises: enrichedExercises, warmup: enrichedWarmup }
        const dayIdx = weekNums.indexOf(nextNum)
        setWeekPos(dayIdx >= 0 ? { day: dayIdx + 1, total: weekNums.length } : null)
        setSessionData(sessionWithVideos)
        // Position sauvegardée — y compris avant une fermeture de l'app par iOS.
        try {
          const saved = loadSessionProgress<SavedProgress>(user.id, mapped.id)
          if (saved) {
            if (saved.ph && saved.ph !== "done") {
              // Séance déjà commencée : pas de second « session_started ».
              if (saved.ph !== "overview") hasTrackedStart.current = true
              setPh(saved.ph)
              setPrevPh(saved.prevPh ?? "")
              setEi(Math.min(saved.ei ?? 0, Math.max(0, mapped.exercises.length - 1)))
              setRnd(saved.rnd ?? 1)
              setSd(saved.sd ?? {})
              setWd(saved.wd ?? [])
              setCd(saved.cd ?? [])
              setFd(saved.fd ?? [])
              setTs(saved.ts ?? {})
            }
          }
        } catch { /* ignore */ }
      } catch (err) {
        console.error("[SessionPage] load error:", err)
        setSessionData(null)
      } finally {
        setSessionLoading(false)
      }
    })()
  }, [wizardData, user, wizardLoading])

  useEffect(() => {
    if (ph === "warmup" && !hasTrackedStart.current && sessionData) {
      track("session_started", { session_name: sessionData.displayName })
      hasTrackedStart.current = true
    }
  }, [ph, track, sessionData])

  // Position sauvegardée à chaque changement — en localStorage : iOS peut fermer l'app
  // installée dès qu'on passe sur une autre app, et sessionStorage part avec elle.
  useEffect(() => {
    if (!sessionData || !user || ph === "done") return
    const progress: SavedProgress = { ph, prevPh, ei, rnd, sd, wd, cd, fd, ts }
    saveSessionProgress(user.id, sessionData.id, progress)
  }, [ph, prevPh, ei, rnd, sd, wd, cd, fd, ts, sessionData, user])

  useEffect(() => {
    if (ph !== "done" || notifiedDone.current || !sessionData) return
    notifiedDone.current = true
    setDoneSaving(true)
    const endTime = new Date().toISOString()
    // Séance terminée : position et chronos effacés.
    if (user) clearSessionProgress(user.id, sessionData.id)
    if (programmeRef.current) {
      savePendingSession({
        programme_id: programmeRef.current.id,
        session_number: programmeRef.current.num,
      })
    }
    void (async () => {
      try {
        const { data: { user: u } } = await supabase.auth.getUser()
        if (!u) return

        // Determine if this is a new completion (false = already done → skip duplicate pushes)
        const isNewCompletion = programmeRef.current
          ? await saveSessionCompletion({
              programme_id: programmeRef.current.id,
              programme_session_number: programmeRef.current.num,
              session_meta: {
                displayName: sessionData.displayName,
                method: extractMethod(sessionData.formatLabel, sessionData.displayName),
                estimatedDuration: sessionData.estimatedDuration,
                exerciseCount: sessionData.exercises.length,
                totalSets: (sessionData.totalSets || sessionData.exercises.length) * (sessionData.totalRounds || 1),
                exerciseNames: sessionData.exercises.map(e => e.name.split("(")[0].trimEnd()),
                exercises: sessionData.exercises.map(e => ({
                  name: e.name.split("(")[0].trimEnd(),
                  sets: e.sets,
                  reps: e.reps,
                  weight: computeChargeKg(e.canonicalName, e.charge, oneRm, bodyweightKg, level).movements.map(m => m.weight).join(" + "),
                })),
                end_time: endTime,
              },
            })
          : true

        // Exécution réelle série par série — figée à partir de l'état final `sd`
        // (série cochée), pas d'un recalcul ultérieur. Une seule écriture batch,
        // après saveSessionCompletion, gardée derrière isNewCompletion pour ne
        // jamais dupliquer sur un replay de la même séance déjà complétée.
        if (isNewCompletion && programmeRef.current) {
          const rounds = sessionData.totalRounds || 1
          const setLogRows: SessionSetLogInput[] = []
          sessionData.exercises.forEach((exr, exIdx) => {
            for (let r = 1; r <= rounds; r++) {
              if (!sd[`${r}-${exIdx}`]) continue
              const charge = computeChargeKg(exr.canonicalName, exr.charge, oneRm, bodyweightKg, level)
              const repsParts = splitCombinedField(exr.reps, charge.movements.length)
              const setsCount = parseSetsCount(exr.sets)
              charge.movements.forEach((m, movIdx) => {
                setLogRows.push({
                  programme_id: programmeRef.current!.id,
                  programme_session_number: programmeRef.current!.num,
                  round: r,
                  exercise_index: exIdx,
                  movement_index: movIdx,
                  exercise_name: m.label ?? exr.canonicalName,
                  reps: repsParts[movIdx] ?? exr.reps,
                  tempo: exr.tempo ?? null,
                  charge_kg: m.weightKg,
                  charge_basis: m.basis,
                  per_dumbbell: m.perDumbbell ?? false,
                  sets_count: setsCount,
                  completed_at: endTime,
                })
              })
            }
          })
          await saveSessionSetLogs(setLogRows)
        }

        // Only send pushes once — skip on duplicate completion
        if (isNewCompletion) {
          // La cloche d'abord : son identifiant voyage avec le push de fin de séance,
          // et un clic sur la notification marque exactement cette entrée comme lue.
          const notif = await createNotification({
            type: "post-session",
            title: "Séance terminée",
            body: `Ta séance "${sessionData.displayName}" est enregistrée.`,
            action_url: "/dashboard",
          }).catch((err) => {
            console.error("[SessionPage] cloche de fin de séance non écrite :", err)
            return null
          })
          await Promise.all([
            // session_end scheduled 15s later so DB writes settle before push delivery
            supabase.from("pending_push_notifications").insert([{
              user_id: u.id,
              send_at: new Date(Date.now() + 15000).toISOString(),
              payload: {
                user_id: u.id,
                title: "Séance terminée 💪",
                body: `Séance "${sessionData.displayName}" terminée avec succès.`,
                url: "/dashboard",
                push_type: "session_end",
                notification_id: notif?.id ?? null,
              },
            }]),
            supabase.functions.invoke("schedule-post-session-push", {
              body: { session_label: sessionData.displayName },
            }),
            supabase.from("wizard_submissions").update({ post_session_pending: true }).eq("user_id", u.id),
          ])
        }
      } catch (err) {
        console.error("[SessionPage] done push error:", err)
      } finally {
        setDoneSaving(false)
      }
    })()
  }, [ph, sessionData])

  // ── Test de force : contexte commun à tous les mouvements ──────────────
  //
  // Une séance de test se reconnaît à son NUMÉRO, déclaré dans `programs.testing_sessions`
  // (typiquement 4/20/48 en Débutant, 1/20/48 ailleurs). C'est ce qui manquait : rien ne
  // distinguait jusqu'ici un test d'une séance normale, et un test de mi-bloc pouvait
  // passer inaperçu.
  const sessionNumber = programmeRef.current?.num ?? null
  const isTestingSession = sessionNumber != null && testingSessions.includes(sessionNumber)

  // Phase du test dans le bloc : premier numéro = test initial, dernier = test final, le
  // reste = intermédiaire. Déduite de la position dans `testing_sessions` plutôt que d'un
  // index en dur, pour ne pas casser si un programme en déclare un nombre différent.
  const testPhase: RmHistoryPhase = (() => {
    if (!isTestingSession || sessionNumber == null) return "manual"
    const idx = testingSessions.indexOf(sessionNumber)
    if (idx === 0) return "initial"
    if (idx === testingSessions.length - 1) return "final"
    return "mid"
  })()

  const testProtocol = useMemo(() => testProtocolForLevel(level), [level])

  // Poids de corps : le relevé le plus récent, à défaut celui de l'inscription. Les charges
  // du test en dépendent (traction, estimation initiale), il vaut mieux une valeur un peu
  // datée que pas de charge du tout.
  const testBodyweightKg = bodyweightKg ?? wizardData?.weight ?? null

  // 1RM de référence du test : valeurs réelles du client, complétées par l'estimation
  // initiale pour les mouvements jamais testés. Les charges des séries 1 et 2 en découlent.
  // Mémoïsé : `buildTestReferenceOneRm` avertit en console quand le questionnaire
  // d'expérience Débutant manque, et le rejouer à chaque rendu noierait le message.
  const testReference = useMemo(() => {
    if (!isTestingSession || !wizardData) return null
    const { bracket } = resolveAgeBracket(wizardData.birth_date, wizardData.age)
    return buildTestReferenceOneRm({
      real: oneRm,
      gender: toStandardsGender(wizardData.gender),
      bracket,
      bodyweightKg: testBodyweightKg,
      level,
      beginnerExperience: level === "beginner" ? wizardData.beginner_experience ?? null : null,
    })
  }, [isTestingSession, wizardData, oneRm, testBodyweightKg, level])

  // Un repos de test DÉMARRE ici, et seulement ici : on efface d'abord toute deadline
  // restée sous la même clé (test précédent dans le même onglet), sans quoi le chrono se
  // monterait déjà échu. Une restauration après rechargement ne passe pas par cette
  // fonction — elle relit `ts` tel quel — et retrouve donc bien sa deadline en cours.
  const startTestRest = (exIdx: number, setIdx: number) => {
    if (sessionData) clearCountdown(strengthTestRestKey(sessionData.id, exIdx, setIdx))
    setTs(prev => ({ ...prev, [exIdx]: { set: setIdx, resting: true } }))
  }

  /**
   * Enregistre le 1RM d'un mouvement, puis enchaîne sur le repos de fin de mouvement.
   *
   * Les deux écritures vont ensemble, comme à la saisie manuelle du Profil :
   * `athlete_profiles` porte la valeur courante (c'est elle que lisent le radar et
   * l'ajustement de cible au test intermédiaire), `rm_history` porte la courbe datée.
   * Sans la première, un test de mi-bloc ne déclencherait PAS le recalcul de cible.
   */
  const recordTestResult = async (exIdx: number, movementKey: keyof typeof RM_FIELD_BY_MOVEMENT, lastSetIndex: number, result: StrengthTestResult) => {
    if (testSaving) return
    setTestSaving(true)
    try {
      const measuredOn = toLocalISODate(new Date())
      const rmField = RM_FIELD_BY_MOVEMENT[movementKey]
      await upsertAthleteProfile({ [rmField]: result.referenceOneRmKg, rm_test_date: measuredOn })
      await recordRmMeasurement({
        movement: movementKey,
        valueKg: result.referenceOneRmKg,
        measuredOn,
        bodyweightKg: testBodyweightKg,
        programmeId: programmeRef.current?.id ?? null,
        blockStartDate: wizardData?.program_week_start ?? null,
        phase: testPhase,
        isMaxEffort: result.isMaxEffort,
      })
      // Le 1RM tout juste mesuré devient la référence des mouvements suivants du test.
      setOneRm(prev => ({ ...(prev ?? { rm_bench_press:null, rm_back_squat:null, rm_deadlift:null, rm_overhead_press:null, rm_pull_up:null }), [rmField]: result.referenceOneRmKg }))
      startTestRest(exIdx, lastSetIndex)
    } catch (err) {
      console.error("[SessionPage] enregistrement du test échoué :", err)
      // L'échec ne doit pas bloquer la séance : on passe au repos comme si de rien n'était.
      // Mais on le DIT — sans quoi le client repartirait en croyant son record enregistré.
      toast.error("Record non enregistré", {
        description: "Note ta valeur et ressaisis-la depuis ton Profil.",
      })
      startTestRest(exIdx, lastSetIndex)
    } finally {
      setTestSaving(false)
    }
  }

  const goTo = (next: string) => { setPrevPh(ph); setPh(next) }

  // Loading state
  if (sessionLoading || subLoading) {
    return (
      <div style={{ maxWidth:430,margin:"0 auto",minHeight:"100vh",background:"#060a13",display:"flex",flexDirection:"column" as const,alignItems:"center",justifyContent:"center",gap:16,fontFamily:"'Inter',-apple-system,sans-serif" }}>
        <div style={{ width:40,height:40,borderRadius:"50%",border:"2px solid rgba(6,182,212,0.2)",borderTopColor:"#06b6d4",animation:"spin 0.9s linear infinite" }} />
        <p style={{ color:"#64748b",fontSize:13 }}>Chargement de ta séance…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    )
  }

  // Plus de garde « séance n° ≥ 2 » : pendant la semaine d'essai tout est ouvert,
  // après elle tout est fermé (D2). C'est RequireSubscription, sur la route, qui
  // tranche — la séance n'a plus à se demander laquelle elle est.

  // No programme found
  if (!sessionData) {
    return (
      <div style={{ maxWidth:430,margin:"0 auto",minHeight:"100vh",background:"#060a13",display:"flex",flexDirection:"column" as const,alignItems:"center",justifyContent:"center",gap:20,padding:"32px 24px",fontFamily:"'Inter',-apple-system,sans-serif",textAlign:"center" as const }}>
        <div style={{ fontSize:40 }}>🏗️</div>
        <h2 style={{ fontSize:20,fontWeight:700,color:"#e2e8f0",margin:0 }}>Programme bientôt disponible</h2>
        <p style={{ fontSize:14,color:"#64748b",lineHeight:1.6,margin:0 }}>Ton programme personnalisé est en cours de configuration. Reviens dans quelques instants.</p>
        <button onClick={() => nav("/dashboard")} style={{ marginTop:8,padding:"13px 32px",border:"none",borderRadius:12,background:"linear-gradient(135deg,#0369a1,#06b6d4)",color:"#fff",fontWeight:600,fontSize:14,cursor:"pointer" }}>Retour à l'accueil</button>
      </div>
    )
  }

  const s = sessionData
  const nEx = s.exercises.length
  const ex = s.exercises[ei]
  const nW = s.warmup.length
  const hasFinisher = s.finisher && s.finisher.length > 0
  const nFinisher = hasFinisher ? s.finisher!.length : 0
  const nT = nW + nEx * (s.totalRounds || 1) + nFinisher + s.cooldown.length

  const prog = (() => {
    let c = wd.length
    const rounds = s.totalRounds || 1
    for (let r = 1; r <= rounds; r++)
      for (let e = 0; e < nEx; e++)
        if (sd[`${r}-${e}`]) c++
    return ((c + fd.length + cd.length) / nT) * 100
  })()
  const method = methodOfDay(s)

  const tr = (fn: () => void) => { setAn(true); setTimeout(() => { fn(); setAn(false) }, 180) }
  const tg = (a: number[], st: React.Dispatch<React.SetStateAction<number[]>>, i: number) =>
    st(a.includes(i) ? a.filter(x => x !== i) : [...a, i])

  const advance = () => {
    const rounds = s.totalRounds || 1
    if (ei < nEx - 1) tr(() => setEi(i => i + 1))
    else if (rounds > 1 && s.interRoundRest) { clearCountdown(`${s.id}|interround-${rnd}`); goTo("interRound") }
    else if (hasFinisher) tr(() => goTo("finisher"))
    else tr(() => goTo("cooldown"))
  }

  const mark = () => {
    setSd(p => ({ ...p, [`${rnd}-${ei}`]: true }))
    advance()
  }

  // ─── Shared styles ───────────────────────────────────────
  const W: React.CSSProperties = { maxWidth:430,margin:"0 auto",minHeight:"100vh",background:T.bg,color:T.t1,fontFamily:"'Inter',-apple-system,sans-serif",position:"relative" }
  const H: React.CSSProperties = { display:"flex",alignItems:"center",gap:12,padding:"14px 20px",position:"sticky",top:0,background:"rgba(6,10,19,0.96)",backdropFilter:"blur(16px)",zIndex:50,borderBottom:`1px solid ${T.bdL}` }
  const A: React.CSSProperties = { opacity:an ? 0 : 1,transform:an ? "translateX(-12px)" : "translateX(0)",transition:"all 0.18s ease" }
  const bar = <div style={{ height:2,background:"rgba(255,255,255,0.03)" }}><div style={{ height:"100%",width:`${prog}%`,background:T.cy,transition:"width 0.5s ease" }} /></div>
  const cta = (l: ReactNode, fn: () => void, ok = true) => <div style={{ position:"fixed",bottom:0,left:0,right:0,padding:"16px 20px 34px",background:"linear-gradient(transparent,#060a13 40%)",zIndex:50,display:"flex",justifyContent:"center" }}><button onClick={fn} disabled={!ok} style={{ width:"100%",maxWidth:390,padding:"16px 0",border:"none",borderRadius:14,fontWeight:700,fontSize:15,color:"#fff",cursor:ok ? "pointer" : "default",background:ok ? `linear-gradient(135deg,#0369a1,${T.cy})` : "rgba(255,255,255,0.05)",opacity:ok ? 1 : 0.35,transition:"all 0.3s",animation:ok ? "pulse 2.5s ease-in-out infinite" : "none" }}>{l}</button></div>
  const css = <style>{CSS}</style>

  // ═══ OVERVIEW ═══════════════════════════════════════════
  if (ph === "overview") {
    const colors = muscleColors(s.exercises)
    const warmupMin = s.warmup.reduce((acc, b) => acc + minutesIn(b.duration), 0)
    // Durée du retour au calme : lue dans le texte complet, pour qu'un « 5 min vélo +
    // 5 min rameur » affiche 10 min et pas seulement la première durée trouvée.
    // `duration` additionne déjà les durées du texte (voir `mapCooldown`) : le badge de
    // section n'a plus qu'à les totaliser sur les étapes.
    const cooldownMin = s.cooldown.reduce((acc, st) => acc + minutesIn(st.duration), 0)
    const titleSize = s.displayName.length > 12 ? 36 : s.displayName.length > 7 ? 46 : 58

    return (
    <div style={{ ...W,overflowX:"hidden" as const }}>
      <div style={{ position:"absolute" as const,top:0,left:"50%",transform:"translateX(-50%)",width:420,height:380,background:"radial-gradient(ellipse at 50% 30%,rgba(6,182,212,0.09) 0%,transparent 65%)",pointerEvents:"none" as const }} />
      <div style={{ padding:"16px 20px 0",position:"relative" as const,zIndex:2 }}><button onClick={() => nav("/dashboard")} style={{ background:"none",border:"none",color:T.t1,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:13,fontWeight:600 }}>{I.back} <span>Accueil</span></button></div>
      <div style={{ padding:"18px 20px 130px",position:"relative" as const }}>

        {/* En-tête : badge du jour, titre, muscles, position dans la semaine */}
        <div style={{ textAlign:"center" as const,animation:"fadeUp 0.5s ease",marginBottom:24,position:"relative" as const }}>
          <div style={{ display:"inline-flex",alignItems:"center",gap:7,padding:"6px 12px",borderRadius:999,border:`1px solid ${T.cy}59`,background:`${T.cy}12`,marginBottom:14 }}>
            <span style={{ width:6,height:6,borderRadius:"50%",background:T.cyanBright,boxShadow:`0 0 8px ${T.cyanBright}` }} />
            <span style={{ fontSize:11,fontWeight:700,letterSpacing:1.4,textTransform:"uppercase" as const,color:T.cyanBright }}>Séance du jour</span>
            <span style={{ fontSize:11,color:T.t3 }}>·</span>
            <span style={{ fontSize:11,fontWeight:500,color:T.t2 }}>{todayLabel()}</span>
          </div>
          <div style={{ position:"relative" as const }}>
            {/* Filigrane : le titre en très grand derrière lui-même, purement décoratif. */}
            <div aria-hidden style={{ position:"absolute" as const,left:"50%",top:"50%",transform:"translate(-50%,-50%)",fontFamily:T.font,fontSize:titleSize * 2,fontWeight:700,lineHeight:1,letterSpacing:-2,whiteSpace:"nowrap" as const,color:"rgba(148,163,184,0.045)",textTransform:"uppercase" as const,pointerEvents:"none" as const,userSelect:"none" as const }}>{s.displayName}</div>
            <h1 style={{ position:"relative" as const,fontFamily:T.font,fontSize:titleSize,fontWeight:700,letterSpacing:-0.5,lineHeight:1.05,color:"#fff",textTransform:"uppercase" as const,textShadow:"0 2px 24px rgba(6,182,212,0.18)" }}>{s.displayName}</h1>
          </div>
          {colors.size > 0 && (
            <div style={{ display:"flex",justifyContent:"center",flexWrap:"wrap" as const,gap:6,marginTop:12 }}>
              {[...colors].map(([muscle, c]) => (
                <span key={muscle} style={{ display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:999,border:`1px solid ${c}55`,background:`${c}14`,fontSize:11.5,fontWeight:600,color:c }}>
                  <span style={{ width:5,height:5,borderRadius:"50%",background:c }} />{muscle}
                </span>
              ))}
            </div>
          )}
          <p style={{ fontFamily:MONO,fontSize:11.5,color:T.t3,marginTop:12,letterSpacing:0.3 }}>
            Semaine {s.weekNumber} · Séance {s.sessionNumber}{weekPos && ` · J${weekPos.day} sur ${weekPos.total}`}
          </p>
        </div>

        {/* Séance de test : annoncée dès l'aperçu, pour que le client sache à quoi s'attendre
            avant de commencer — c'est précisément ce qui manquait quand un test de mi-bloc
            passait inaperçu. */}
        {isTestingSession && (
          <div style={{ background:`${T.or}0d`,border:`1px solid ${T.or}30`,borderRadius:14,padding:"14px 16px",marginBottom:24,animation:"fadeUp 0.5s ease 0.03s both" }}>
            <div style={{ display:"flex",alignItems:"center",gap:7,marginBottom:6 }}>
              <span style={{ color:T.or,display:"flex" }}>{I.fire}</span>
              <span style={{ fontSize:13,fontWeight:700,color:T.or }}>Test de force</span>
            </div>
            <p style={{ fontSize:12.5,color:T.t2,lineHeight:1.6,margin:0 }}>
              Trois montées par mouvement : {testProtocol[0].reps} reps à {testProtocol[0].percentOfOneRm} %,
              {" "}{testProtocol[1].reps} reps à {testProtocol[1].percentOfOneRm} %, puis une série maximale.
              Ton 1RM est calculé automatiquement et enregistré dans tes records.
            </p>
          </div>
        )}

        {/* Vue d'ensemble — stats et méthode, avant l'échauffement (ordre de la maquette) */}
        <div style={{ marginBottom:28,animation:"fadeUp 0.5s ease 0.04s both" }}>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8 }}>
            <StatCard icon={I.clock} label="Durée" value={s.estimatedDuration} accent={T.cyanBright} />
            <StatCard icon={I.db} label="Exercices" value={String(nEx)} accent={T.pink} />
            <StatCard icon={I.loop} label={s.totalRounds ? "Rounds" : "Séries"} value={String(s.totalRounds || s.totalSets || "-")} accent={T.green} />
          </div>
          {method && (
            <div style={{ background:T.s1,border:`1px solid ${T.violet}33`,borderRadius:14,padding:"12px 14px",marginTop:10,position:"relative" as const,overflow:"hidden" as const }}>
              <div style={{ position:"absolute" as const,inset:0,background:`linear-gradient(90deg,${T.violet}14,transparent 60%)`,pointerEvents:"none" as const }} />
              <div style={{ position:"relative" as const,display:"flex",alignItems:"center",gap:6,marginBottom:6 }}>
                <span style={{ width:20,height:20,borderRadius:6,display:"grid",placeItems:"center",color:T.violet,background:`${T.violet}1f`,border:`1px solid ${T.violet}45` }}>{I.bolt}</span>
                <span style={{ fontSize:10.5,fontWeight:700,letterSpacing:1.1,textTransform:"uppercase" as const,color:T.violet }}>Méthode du jour</span>
              </div>
              <div style={{ position:"relative" as const,fontSize:14,fontWeight:600,color:T.txHi,lineHeight:1.45 }}>{method}</div>
            </div>
          )}
        </div>

        {/* Échauffement */}
        <div style={{ animation:"fadeUp 0.5s ease 0.05s both" }}>
          <SectionHead title="Échauffement" color={T.or} badge={warmupMin > 0 ? `${warmupMin} min` : undefined} />
          <div style={{ background:T.s1,border:`1px solid ${T.line}`,borderRadius:14,padding:"4px 14px" }}>
            {s.warmup.map((b, i) => (
              <div key={i} style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderTop:i > 0 ? `1px solid ${T.line}` : "none" }}>
                <span style={{ width:22,height:22,flexShrink:0,borderRadius:6,display:"grid",placeItems:"center",fontFamily:MONO,fontSize:11,fontWeight:700,color:T.or,background:`${T.or}1a`,border:`1px solid ${T.or}40` }}>{i + 1}</span>
                <span style={{ fontSize:14,fontWeight:600,flex:1,color:T.t1 }}>{b.label}</span>
                {b.rounds && <><span style={{ color:T.t3,display:"flex" }}>{I.loop}</span><span style={{ fontSize:12,fontWeight:600,color:T.t2 }}>{b.rounds}</span></>}
                <DurationChip text={b.duration} />
              </div>
            ))}
          </div>
        </div>

        {/* Corps de séance */}
        <div style={{ marginTop:28,animation:"fadeUp 0.5s ease 0.1s both" }}>
          <SectionHead title="Corps de séance" color={T.cy} />
          <div style={{ display:"flex",flexDirection:"column" as const,gap:8 }}>
            {s.exercises.map((e, i) => {
              const c = colors.get(muscleGroup(e)) ?? T.cy
              const rx = prescription(e, computeChargeKg(e.canonicalName, e.charge, oneRm, bodyweightKg, level))
              const m = e.setSchemeSpec?.method
              return (
                <div key={i} style={{ position:"relative" as const,overflow:"hidden" as const,display:"flex",gap:12,background:T.s1,border:`1px solid ${T.line}`,borderLeft:`3px solid ${c}`,borderRadius:14,padding:"12px 14px 12px 12px" }}>
                  <div style={{ position:"absolute" as const,inset:0,background:`linear-gradient(90deg,${c}14,transparent 45%)`,pointerEvents:"none" as const }} />
                  <span style={{ position:"relative" as const,fontFamily:MONO,fontSize:13,fontWeight:700,color:T.t3,minWidth:14,paddingTop:1 }}>{e.order}</span>
                  <div style={{ position:"relative" as const,flex:1,minWidth:0 }}>
                    <div style={{ fontSize:14.5,fontWeight:700,color:T.txHi,lineHeight:1.3 }}>{e.canonicalName}</div>
                    <div style={{ display:"flex",alignItems:"center",flexWrap:"wrap" as const,gap:8,marginTop:5 }}>
                      {e.primaryMuscle && (
                        <span style={{ display:"inline-flex",alignItems:"center",gap:5,fontSize:12,fontWeight:500,color:c }}>
                          <span style={{ width:5,height:5,borderRadius:"50%",background:c }} />{e.primaryMuscle}
                        </span>
                      )}
                      {m && (
                        <span style={{ fontFamily:MONO,fontSize:9.5,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase" as const,color:METHOD_COLOR[m],background:`${METHOD_COLOR[m]}17`,border:`1px solid ${METHOD_COLOR[m]}50`,borderRadius:5,padding:"2px 6px" }}>{METHOD_LABEL[m].long}</span>
                      )}
                    </div>
                    <div style={{ fontFamily:MONO,fontSize:12,marginTop:7,lineHeight:1.4,wordBreak:"break-word" as const }}>
                      <span style={{ color:T.t2 }}>{rx.reps}</span>
                      {rx.load && <><span style={{ color:T.t3 }}> · </span><span style={{ color:T.green }}>{rx.load}</span></>}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Finisher (conditionnel) — ambre, pour ne pas se confondre avec l'échauffement orange */}
        {hasFinisher && (
          <div style={{ marginTop:28,animation:"fadeUp 0.5s ease 0.15s both" }}>
            <SectionHead title="Finisher" color="#facc15" icon={I.fire} />
            <div style={{ background:T.s1,border:`1px solid ${T.line}`,borderRadius:14,padding:"4px 14px" }}>
              {s.finisher!.map((st, i) => (
                <div key={i} style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderTop:i > 0 ? `1px solid ${T.line}` : "none" }}>
                  <span style={{ width:22,height:22,flexShrink:0,borderRadius:6,display:"grid",placeItems:"center",fontFamily:MONO,fontSize:11,fontWeight:700,color:"#facc15",background:"#facc151a",border:"1px solid #facc1540" }}>{i + 1}</span>
                  <span style={{ fontSize:14,fontWeight:600,color:T.t1 }}>{st.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Retour au calme — la durée vit uniquement dans le badge de section ; la ligne
            dit ce qu'on fait, tel que le programme l'écrit. */}
        <div style={{ marginTop:28,animation:"fadeUp 0.5s ease 0.2s both" }}>
          <SectionHead title="Retour au calme" color={T.violet} badge={cooldownMin > 0 ? `${cooldownMin} min` : undefined} />
          <div style={{ background:T.s1,border:`1px solid ${T.line}`,borderRadius:14,padding:"4px 14px" }}>
            {s.cooldown.map((st, i) => (
              <div key={i} style={{ display:"flex",alignItems:"flex-start",gap:12,padding:"12px 0",borderTop:i > 0 ? `1px solid ${T.line}` : "none" }}>
                <span style={{ width:6,height:6,borderRadius:"50%",background:T.violet,boxShadow:`0 0 8px ${T.violet}88`,flexShrink:0,marginTop:7 }} />
                {/* Aperçu : le résumé court quand le texte enchaîne plusieurs activités,
                    et rien d'autre — pas de durée par activité, pas d'intensité, pas de
                    variante matériel. Le détail complet est à l'étape elle-même. */}
                <div style={{ flex:1,minWidth:0,fontSize:14,fontWeight:600,color:T.t1,lineHeight:1.45 }}>
                  {st.summary ?? st.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Même `cta()` que toutes les autres fins de page de la séance, plutôt qu'un
          bouton recopié à côté : c'est cette copie qui avait dérivé (cyan uni, 16 px,
          rayon 16) pendant que le reste de l'écran gardait le dégradé bleu → cyan. Le
          bouton d'entrée dans la séance n'a aucune raison de se distinguer des suivants
          — et passer par le helper est ce qui garantit qu'il ne redivergera pas. */}
      {cta(<span style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:8 }}>C'est parti {I.chev}</span>, () => tr(() => goTo("warmup")))}
      {css}
    </div>
    )
  }

  // ═══ WARMUP ═════════════════════════════════════════════
  if (ph === "warmup") return (
    <div style={W}><div style={H}><button onClick={() => goTo("overview")} style={{ background:"none",border:"none",color:T.t2,cursor:"pointer",display:"flex" }}>{I.back}</button><span style={{ fontSize:15,fontWeight:600 }}>Échauffement</span><span style={{ marginLeft:"auto",fontSize:12,color:T.t3 }}>{wd.length}/{nW}</span></div>{bar}
      <div style={{ padding:"20px 20px 130px",...A }}>
        {s.warmup.map((b, bi) => { const isDone = wd.includes(bi); return (
          <div key={bi} style={{ marginBottom:20 }}>
            <div onClick={() => tg(wd, setWd, bi)} style={{ background:isDone ? `${T.gn}08` : T.sf, border:`1px solid ${isDone ? T.gn + "40" : T.bd}`, borderRadius:14,padding:"14px 16px",cursor:"pointer",transition:"all 0.2s" }}>
              <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                <div style={{ width:24,height:24,borderRadius:7,flexShrink:0,background:isDone ? T.gn : "transparent",border:isDone ? "none" : `1.5px solid ${T.t3}`,display:"flex",alignItems:"center",justifyContent:"center",color:isDone ? "#fff" : "transparent",transition:"all 0.2s" }}>{I.chk}</div>
                <span style={{ fontSize:15,fontWeight:600,flex:1,opacity:isDone ? 0.5 : 1 }}>{b.label}</span>
                {b.rounds && <><span style={{ color:T.t3 }}>{I.loop}</span><span style={{ fontSize:12,fontWeight:600,color:T.t2 }}>{b.rounds}</span></>}
                <Badge text={b.duration} />
              </div>
            </div>
            <div style={{ padding:"8px 12px 0 12px" }}>
              {b.exercises.map((e, i) => (
                <div key={i} style={{ padding:"6px 0" }}>
                  <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                    {/* minWidth:0 + overflowWrap : un texte reps long ("2x5 (band assisted si besoin)") passe à la ligne dans sa colonne et agrandit la ligne, sans déborder sur le nom */}
                    <span style={{ fontSize:12,fontWeight:600,color:T.cy,width:56,minWidth:0,textAlign:"center" as const,flexShrink:0,fontVariantNumeric:"tabular-nums" as const,overflowWrap:"anywhere" as const }}>{e.reps}</span>
                    <span style={{ fontSize:12,color:T.t2,flex:1,minWidth:0,textAlign:"center" as const,overflowWrap:"anywhere" as const }}>{e.name}</span>
                    <div style={{ width:56,display:"flex",justifyContent:"center",flexShrink:0 }}>
                      {e.videoUrl ? (
                        <button onClick={() => setFsV(e.videoUrl!)} aria-label={`Voir la vidéo : ${e.name}`} style={{ background:"none",border:"none",padding:6,margin:-6,color:T.cy,cursor:"pointer",display:"flex" }}>{I.filmLg}</button>
                      ) : (
                        <div style={{ color:T.cy,opacity:0.25,display:"flex" }}>{I.filmLg}</div>
                      )}
                    </div>
                  </div>
                  {/* Repli matériel du programme maison ("… si pas de rameur") : une ligne
                      à part, sous la consigne, jamais fondue dedans ni masquée — l'app ne
                      sait pas de quel matériel le client dispose. */}
                  {e.variant && (
                    <div style={{ display:"flex",gap:7,margin:"4px 56px 0 56px",fontSize:11.5,color:T.t3,lineHeight:1.45 }}>
                      <span style={{ flexShrink:0,fontWeight:600,color:T.t2 }}>Variante</span>
                      <span style={{ minWidth:0,overflowWrap:"anywhere" as const }}>{e.variant}</span>
                    </div>
                  )}
                </div>
              ))}
              {b.restBetweenRounds && b.restBetweenRounds > 0 && (
                <div style={{ marginTop:8 }}><TimerButton label="Lancer le repos" sec={b.restBetweenRounds} accent={T.gn} /></div>
              )}
            </div>
          </div>
        ) })}
      </div>
      {cta("Commencer les exercices →", () => tr(() => { goTo("exercise"); setRnd(1); setEi(0) }), wd.length === nW)}
      {fsV && <VideoPlayer url={fsV} fs toggle={() => setFsV(null)} />}
      {css}
    </div>
  )

  // ═══ INTER-ROUND ════════════════════════════════════════
  if (ph === "interRound") {
    const rounds = s.totalRounds || 1
    const isLast = rnd >= rounds
    return (
      <div style={W}><div style={H}><span style={{ fontSize:15,fontWeight:600 }}>Repos inter-rounds</span></div>{bar}
        <div style={{ padding:"48px 20px",textAlign:"center" as const }}>
          <div style={{ display:"inline-flex",alignItems:"center",gap:6,background:`${T.gn}10`,border:`1px solid ${T.gn}28`,borderRadius:20,padding:"5px 14px",marginBottom:28 }}>
            <span style={{ color:T.gn }}>{I.chk}</span><span style={{ color:T.gn,fontSize:13,fontWeight:600 }}>Round {rnd}/{rounds} terminé</span>
          </div>
          <p style={{ fontSize:14,color:T.t2,marginBottom:32 }}>{isLast ? (hasFinisher ? "Finisher dans :" : "Retour au calme dans :") : `Round ${rnd + 1} dans :`}</p>
          <TimerCountdown
            sec={s.interRoundRest || 180}
            restKey={`${s.id}|interround-${rnd}`}
            onDone={() => {
              if (isLast) goTo(hasFinisher ? "finisher" : "cooldown")
              else { setRnd(r => r + 1); setEi(0); goTo("exercise") }
            }}
            label={s.interRoundNature}
          />
        </div>{css}
      </div>
    )
  }

  // ═══ FINISHER ═══════════════════════════════════════════
  if (ph === "finisher" && hasFinisher) return (
    <div style={W}><div style={H}><span style={{ fontSize:15,fontWeight:700 }}>Finisher</span><div style={{ marginLeft:"auto",color:T.or,display:"flex",alignItems:"center",gap:4 }}>{I.fire}<span style={{ fontSize:12,fontWeight:600 }}>Dernier effort</span></div></div>{bar}
      <div style={{ padding:"20px 20px 130px",...A }}>
        <div style={{ background:`${T.or}08`,border:`1px solid ${T.or}22`,borderRadius:14,padding:16,marginBottom:16 }}><p style={{ fontSize:13,color:T.or,fontWeight:600 }}>Enchaîne sans pause. Donne tout !</p></div>
        {s.finisher!.map((st, i) => (
          <div key={i} onClick={() => tg(fd, setFd, i)} style={{ background:fd.includes(i) ? `${T.or}08` : T.sf, border:`1px solid ${fd.includes(i) ? T.or + "40" : T.bd}`, borderRadius:14,padding:"18px 16px",marginBottom:10,cursor:"pointer",transition:"all 0.2s" }}>
            <div style={{ display:"flex",alignItems:"flex-start",gap:12 }}>
              <div style={{ width:24,height:24,borderRadius:7,flexShrink:0,marginTop:2,background:fd.includes(i) ? T.or : "transparent",border:fd.includes(i) ? "none" : `1.5px solid ${T.t3}`,display:"flex",alignItems:"center",justifyContent:"center",color:fd.includes(i) ? "#fff" : "transparent" }}>{I.chk}</div>
              <div style={{ flex:1 }}><div style={{ fontSize:16,fontWeight:700,marginBottom:8 }}>{st.label}</div>
                <div style={{ display:"flex",gap:20 }}>
                  <div><span style={{ fontSize:11,color:T.t3 }}>Reps</span><div style={{ fontSize:15,fontWeight:700,color:"#fff",marginTop:2 }}>{st.reps}</div></div>
                  <div><span style={{ fontSize:11,color:T.t3 }}>Charge</span><div style={{ fontSize:15,fontWeight:700,color:"#fff",marginTop:2 }}>{st.charge}</div></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {cta("Retour au calme →", () => tr(() => goTo("cooldown")), fd.length === nFinisher)}
      {css}
    </div>
  )

  // ═══ COOLDOWN ═══════════════════════════════════════════
  // Flèche de retour de l'en-tête : vers le finisher quand il y en a eu un, sinon vers le
  // dernier exercice du corps de séance. C'est le MÊME recul que d'un exercice au
  // précédent — on repose `rnd`/`ei` sur la dernière case et on rouvre la phase
  // "exercise", sans rien effacer : l'exercice se réaffiche tel quel, entièrement
  // interactif, et son bouton renvoie ici par `advance()`. Une flèche seule, sans
  // libellé, comme sur les écrans d'exercice — contrairement au « ← Accueil » de
  // l'aperçu, qui lui quitte la séance.
  if (ph === "cooldown") return (
    <div style={W}><div style={H}>{prevPh === "finisher"
      ? <button onClick={() => goTo("finisher")} aria-label="Revenir au finisher" style={{ background:"none",border:"none",color:T.t2,cursor:"pointer",display:"flex" }}>{I.back}</button>
      : nEx > 0 && <button onClick={() => tr(() => { setRnd(s.totalRounds || 1); setEi(nEx - 1); goTo("exercise") })} aria-label="Revenir au dernier exercice" style={{ background:"none",border:"none",color:T.t2,cursor:"pointer",display:"flex" }}>{I.back}</button>}<span style={{ fontSize:15,fontWeight:700 }}>Retour au calme</span></div>{bar}
      <div style={{ padding:"20px 20px 130px",...A }}>
        {s.cooldown.map((st, i) => (
          <div key={i} onClick={() => tg(cd, setCd, i)} style={{ background:cd.includes(i) ? `${T.vi}08` : T.sf, border:`1px solid ${cd.includes(i) ? T.vi + "40" : T.bd}`, borderRadius:14,padding:"20px 16px",cursor:"pointer",transition:"all 0.2s" }}>
            <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:8 }}>
              <div style={{ width:24,height:24,borderRadius:7,flexShrink:0,background:cd.includes(i) ? T.vi : "transparent",border:cd.includes(i) ? "none" : `1.5px solid ${T.t3}`,display:"flex",alignItems:"center",justifyContent:"center",color:cd.includes(i) ? "#fff" : "transparent" }}>{I.chk}</div>
              <span style={{ fontSize:16,fontWeight:700,flex:1 }}>{st.label}</span>
              <Badge text={st.duration} />
            </div>
            {/* Le libellé porte désormais le contenu : répéter `detail` juste en dessous
                afficherait deux fois la même consigne. Seule la variante matériel, que
                le libellé ne contient pas, garde sa ligne. */}
            {st.variant && (
              <div style={{ marginLeft:36,display:"flex",gap:7,fontSize:13,color:T.t3,lineHeight:1.45 }}>
                <span style={{ flexShrink:0,fontWeight:600,color:T.t2 }}>Variante</span>
                <span style={{ minWidth:0,overflowWrap:"anywhere" as const }}>{st.variant}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      {cta("Terminer la séance ✓", () => goTo("done"), cd.length === s.cooldown.length)}
      {css}
    </div>
  )

  // ═══ DONE ═══════════════════════════════════════════════
  if (ph === "done") {
    const dayIdx = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000)
    const phrase = `${CONGRATS[dayIdx % CONGRATS.length]} ${firstName} !`
    return (
      <div style={W}><div style={{ display:"flex",flexDirection:"column" as const,alignItems:"center",justifyContent:"center",minHeight:"100vh",padding:"32px 24px",textAlign:"center" as const }}>
        <div style={{ width:"100%",maxWidth:300,height:220,borderRadius:20,overflow:"hidden" as const,marginBottom:28,animation:"fadeUp 0.5s ease",background:"linear-gradient(180deg,#000 0%,#0a0f1a 100%)" }}><img src="/session-complete.jpg" alt="" style={{ width:"100%",height:"100%",objectFit:"cover" as const,opacity:0.85 }} onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }} /></div>
        <h2 style={{ fontSize:20,fontWeight:700,color:T.cy,marginBottom:14,animation:"fadeUp 0.5s ease 0.1s both" }}>{phrase}</h2>
        {/* La confirmation est un REPÈRE, pas un second titre : le félicitations est déjà
            la grande phrase de l'écran, et un « Séance terminée ! » en 24 px juste en
            dessous lui répondait exclamation pour exclamation. Même pastille verte à
            coche que « Round X/Y terminé » du repos inter-rounds — dans cette app, une
            étape qui a abouti se dit comme ça. */}
        <div style={{ display:"inline-flex",alignItems:"center",gap:7,padding:"6px 14px",borderRadius:999,border:`1px solid ${T.gn}3d`,background:`${T.gn}12`,marginBottom:32,animation:"fadeUp 0.5s ease 0.15s both" }}>
          <span style={{ display:"flex",color:T.gn }}>{I.chk}</span>
          <span style={{ fontSize:13,fontWeight:600,color:T.gn,letterSpacing:0.2 }}>Terminée</span>
        </div>
        <button
          disabled={doneSaving}
          onClick={() => { if (!doneSaving) nav("/dashboard") }}
          style={{ width:"100%",padding:"16px 0",border:"none",borderRadius:14,fontWeight:700,fontSize:15,color:"#fff",cursor:doneSaving ? "default" : "pointer",background:`linear-gradient(135deg,#0369a1,${T.cy})`,opacity:doneSaving ? 0.6 : 1,animation:doneSaving ? "none" : "pulse 2.5s ease-in-out infinite",marginBottom:20 }}
        >{doneSaving ? "Enregistrement..." : "Retour à la page d'accueil"}</button>
        <div style={{ width:"100%",background:"rgba(6,182,212,0.06)",border:"1px solid rgba(6,182,212,0.25)",borderRadius:16,padding:"16px 20px",textAlign:"left" as const }}>
          <p style={{ fontSize:13,fontWeight:700,color:T.cy,marginBottom:8 }}>⏰ Questionnaire dans 30 min</p>
          <p style={{ fontSize:13,color:T.t2,lineHeight:1.65,margin:0 }}>Tu recevras une notification pour évaluer ta séance — indispensable pour suivre ta charge d'entraînement et débloquer ta prochaine séance.</p>
        </div>
      </div>{css}</div>
    )
  }

  // ═══ EXERCISE ═══════════════════════════════════════════
  const done = sd[`${rnd}-${ei}`]
  const rounds = s.totalRounds || 1

  // ─── Schéma de série ────────────────────────────────────────
  // Purement informatif : il montre la STRUCTURE de la série, il ne pilote rien. La
  // validation reste au niveau de l'exercice entier, comme avant ce chantier.
  //
  // Résolu par un appel simple, pas par un useMemo : on est ici APRÈS les retours
  // anticipés de phase (overview, warmup, done...), donc un hook à cet endroit ne
  // serait appelé que sur la phase "exercise" — l'ordre des hooks changerait d'un
  // rendu à l'autre. Le coût est de toute façon négligeable : quelques paliers.
  const scheme = ex.setSchemeSpec
    ? resolveSetScheme(ex.setSchemeSpec, ex.canonicalName, oneRm, bodyweightKg, level)
    : null

  // ── Parcours de test, pour CE mouvement ───────────────────────────────
  // `resolveTestedLift` retombe sur null quand l'exercice ne mesure aucun des cinq
  // mouvements de référence : on affiche alors la tuile d'exercice normale plutôt que
  // de bloquer la séance sur un test impossible à interpréter.
  const testedLift = isTestingSession ? resolveTestedLift(ex.canonicalName) : null
  // Le protocole du JSON prime — c'est là que le contenu se règle. Le protocole par
  // niveau ne sert que de repli pour un exercice dont le JSON ne déclare rien (ou déclare
  // quelque chose d'illisible).
  const exProtocol = ex.testProtocol ?? testProtocol
  const testStep = ts[ei] ?? { set: 0, resting: false }
  const testBlock = testedLift && testReference && !done ? (
    <StrengthTestBlock
      ex={ex}
      sessionId={s.id}
      exerciseIndex={ei}
      lift={testedLift}
      protocol={exProtocol}
      setIndex={Math.min(testStep.set, exProtocol.length - 1)}
      resting={testStep.resting}
      oneRm={testReference.oneRm}
      referenceIsEstimated={testReference.estimated.has(testedLift.movement)}
      bodyweightKg={testBodyweightKg}
      level={level}
      previousReferenceOneRmKg={oneRm?.[testedLift.rmField] ?? null}
      saving={testSaving}
      onRestDone={() => {
        // Fin du repos : soit on passe à la série suivante du même mouvement, soit le
        // mouvement est terminé et on enchaîne comme une série normale (mark → advance).
        if (testStep.set >= exProtocol.length - 1) {
          setTs(prev => ({ ...prev, [ei]: { set: 0, resting: false } }))
          mark()
        } else {
          setTs(prev => ({ ...prev, [ei]: { set: testStep.set + 1, resting: false } }))
        }
      }}
      onRecord={(result) => { void recordTestResult(ei, testedLift.movement, exProtocol.length - 1, result) }}
    />
  ) : null

  // Séries 1 et 2 : le bouton fixe du bas valide la série et lance le repos. Série
  // maximale : il disparaît, le formulaire porte ses propres boutons (« Calculer mon
  // 1RM », puis « Enregistrer et continuer ») — deux boutons d'action concurrents en bas
  // d'écran, c'est la garantie d'un enregistrement raté.
  const testCtaVisible =
    testBlock != null && !testStep.resting && !exProtocol[Math.min(testStep.set, exProtocol.length - 1)].maxEffort

  return (
    <div style={W}>
      {fsV && <VideoPlayer url={fsV} fs toggle={() => setFsV(null)} />}
      <div style={H}>
        <button onClick={() => {
          if (ei === 0 && rnd === 1) goTo("warmup")
          else if (ei > 0) tr(() => setEi(i => i - 1))
          else { setRnd(r => r - 1); tr(() => setEi(nEx - 1)) }
        }} style={{ background:"none",border:"none",color:T.t2,cursor:"pointer",display:"flex" }}>{I.back}</button>
        <div style={{ flex:1,textAlign:"center" as const }}>
          {rounds > 1 && <div style={{ fontSize:10,color:T.t3,fontWeight:600,letterSpacing:1,textTransform:"uppercase" as const }}>Round {rnd}/{rounds}</div>}
          <div style={{ fontSize:14,fontWeight:600 }}>{isTestingSession ? "Mouvement" : "Exercice"} {ei + 1}/{nEx}</div>
        </div>
        <div style={{ width:20 }} />
      </div>
      {bar}

      {/* Dots */}
      <div style={{ display:"flex",justifyContent:"center",gap:5,padding:"12px 20px" }}>
        {s.exercises.map((_, i) => {
          const d = sd[`${rnd}-${i}`]; const c = i === ei
          return <div key={i} style={{ width:c ? 20 : 7,height:7,borderRadius:4,background:d ? T.cy : c ? T.cy + "50" : "rgba(255,255,255,0.05)",transition:"all 0.3s" }} />
        })}
      </div>

      <div style={{ padding:"0 20px 140px",...A }}>
        <ExerciseView ex={ex} oneRm={oneRm} bodyweightKg={bodyweightKg} level={level} onOpenVideo={url => setFsV(url)} restKey={`${s.id}|${rnd}-${ei}`} testBlock={testBlock ?? undefined} scheme={scheme} />
      </div>

      {testBlock
        ? testCtaVisible && cta(
            <span style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:8 }}>{I.chk} Série terminée</span>,
            () => startTestRest(ei, Math.min(testStep.set, exProtocol.length - 1))
          )
        : cta(
            done ? "Exercice suivant →" : <span style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:8 }}>{I.chk} Série terminée</span>,
            done ? advance : mark
          )}
      {css}
    </div>
  )
}
